/**
 * SignInModal — Google OAuth only.
 *
 * Magic-link UI removed for the May 18 launch (the Resend domain
 * verification is blocked by the Wix DNS sub-domain MX limitation;
 * waiting for the Porkbun transfer ~Jun 26 to fix that). The server
 * routes at /api/auth/magic-link still exist and work — we just don't
 * surface them in the UI. Google OAuth carries 100% of sign-ins.
 *
 * After Jun 26 / DNS fix, re-enable the email path by restoring the
 * `<form onSubmit={handleEmailSubmit}>` block and the toggle button.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";

interface SignInModalProps {
  open: boolean;
  onClose: () => void;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

export function SignInModal({ open, onClose }: SignInModalProps) {
  const { t } = useTranslation();
  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const [founder, setFounder] = useState<{ remaining: number; limit: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    api.founderStatus().then(setFounder).catch(() => undefined);
  }, [open]);

  if (!open) return null;

  const handleGoogleClick = () => {
    // Full-page redirect — Google's consent page can't be loaded in an iframe.
    window.location.href = `${API_BASE}/api/auth/google`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-title"
      onClick={onClose}
    >
      <div
        className="w-[min(24rem,90%)] rounded-3xl bg-emerald-950/95 p-6 text-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 id="signin-title" className="text-xl font-bold">
            Sign in to Curvle
          </h2>
          <button
            type="button"
            aria-label="Close"
            className="opacity-60 hover:opacity-100"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <p className="mt-2 text-sm opacity-75">
          Sign in to keep your streak across devices.
        </p>

        {founder && founder.remaining > 0 && (
          <p className="mt-3 rounded-xl bg-amber-300/15 px-3 py-2 text-center text-xs font-semibold text-amber-100 ring-1 ring-amber-300/30">
            {t("founder.signin_banner", { limit: founder.limit.toLocaleString(), remaining: founder.remaining.toLocaleString() })}
          </p>
        )}

        {/* Primary (and only) CTA — Google OAuth */}
        <button
          type="button"
          className="mt-4 flex w-full items-center justify-center gap-3 rounded-full bg-white px-5 py-2.5 font-semibold text-emerald-900 transition hover:bg-white/90"
          onClick={handleGoogleClick}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.331C2.438 15.983 5.482 18 9 18z"/>
            <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
          </svg>
          Continue with Google
        </button>

        <p className="mt-3 text-center text-xs opacity-50">
          Free. No password needed. Powered by your Google account.
        </p>
      </div>
    </div>
  );
}
